import { action, computed, observable } from 'mobx'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { type SingleSelectFilter } from '@/infra/SingleSelectFilter'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import type {
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetWorkspaceSelectionFn,
} from '@/features/iam'
import type { WorkspaceSearcher } from '@/features/iam/workspaces/WorkspaceSearcher'
import {
  type LastWorkspaceStorage,
  ALL_WORKSPACES_SELECTION,
} from '@/features/iam/workspaces/LastWorkspaceStorage'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type Storage } from '@/infra/storage'
import { tapAction, toStream } from '@/utils/mobx-util'

export class WorkspaceSelectorViewModel
  implements WorkspaceSearcher, ActivationLifecycle
{
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _textSearcher = new SearchTextViewModel(500)

  private readonly _globalSnapshotKey = 'globalWorkspaceSnapshot.userManagement'

  @observable
  accessor editing = false

  /**
   * Follows the global workspace selector.
   */
  private readonly _globalWorkspaceChanges$ = toStream(
    () => this._getWorkspaceSelection().workspace?.id,
  ).pipe(
    tapAction((workspaceId) => {
      const workspace =
        workspaceId && this.workspaces.find((w) => w.id === workspaceId)
      if (workspace) {
        this.selectWorkspace(workspace)
      }
    }),
  )

  constructor(
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _workspaceFilter: () => SingleSelectFilter<
      string,
      Workspace
    >,
    private readonly _clearSelectedUsers: () => void,
    private readonly _clearWorkspaceScopedFilters: () => void,
    private readonly _getWorkspaceSelection: GetWorkspaceSelectionFn,
    private readonly _lastWorkspaceStorage: LastWorkspaceStorage,
    private readonly _getUserId: () => string,
    private readonly _storage: Storage,
  ) {
    this._lifecycle.manage(this._textSearcher.debouncedValue$)
    this._lifecycle.manage(this._globalWorkspaceChanges$)
  }

  activate() {
    this._lifecycle.activate()
  }

  setDefaultWorkspace() {
    // The global "default to all workspaces" setting wins over the saved
    // selection, so every page load starts on all workspaces.
    if (this.canManageAll && this._getWorkspaceSelection().allWorkspaces) {
      return this.selectWorkspace(Workspace.none, false)
    }

    // A global workspace picked since the last visit wins over this page's
    // saved selection.
    const currentGlobalId = this._getWorkspaceSelection().workspace?.id
    const savedGlobalSnapshot = this._storage.getItem(this._globalSnapshotKey)
    const globalChanged =
      currentGlobalId && savedGlobalSnapshot !== currentGlobalId

    if (globalChanged) {
      const matchedWorkspace = this.workspaces.find(
        (w) => w.id === currentGlobalId,
      )
      if (matchedWorkspace) {
        return this.selectWorkspace(matchedWorkspace)
      }
    }

    const lastWorkspaceId = this._lastWorkspaceStorage.load(this._getUserId())
    if (lastWorkspaceId) {
      if (lastWorkspaceId === ALL_WORKSPACES_SELECTION && this.canManageAll) {
        return this.selectWorkspace(Workspace.none)
      }

      const lastWorkspace = this.workspaces.find(
        (w) => w.id === lastWorkspaceId,
      )
      if (lastWorkspace) {
        return this.selectWorkspace(lastWorkspace)
      }
    }

    if (currentGlobalId) {
      const matchedWorkspace = this.workspaces.find(
        (w) => w.id === currentGlobalId,
      )
      if (matchedWorkspace) {
        return this.selectWorkspace(matchedWorkspace)
      }
    }

    const memberWorkspace = this._getCurrentMember().workspace

    return this.selectWorkspace(
      this.workspaces.some((workspace) => workspace.id === memberWorkspace.id)
        ? memberWorkspace
        : this.workspaces[0],
    )
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  @computed
  get searchText() {
    return this._textSearcher.value
  }

  @computed
  get selectedWorkspace() {
    return this._workspaceFilter().value
  }

  @computed
  get filteredWorkspaces() {
    const searchText = this.searchText.toLowerCase()

    if (searchText) {
      return this.workspaces.filter((ws) =>
        ws.name.toLocaleLowerCase().includes(searchText),
      )
    }

    return this.workspaces
  }

  @computed
  get canManageAll() {
    return this._getCurrentUserWorkspaceMemberships().some(
      ({ member }) => member.permissions.adminGlobalUserList,
    )
  }

  @computed
  get workspaces() {
    return this._getCurrentUserWorkspaceMemberships()
      .filter(({ member }) => member.permissions.adminUserList)
      .map((member) => member.workspace)
  }

  @computed
  get canInviteUsers() {
    return this._getCurrentUserWorkspaceMemberships().some(({ member }) => {
      const hasAdminInvite =
        member.permissions.adminRoleList &&
        member.permissions.adminTeamsView &&
        member.permissions.adminUserCreate
      const externalCustomerConfig = member.workspace.config.externalCustomer
      const hasCustomerInvite =
        member.permissions.customerCreate &&
        externalCustomerConfig.enabled &&
        externalCustomerConfig.allowRequestingMagicLinks

      return hasAdminInvite || hasCustomerInvite
    })
  }

  @action.bound
  selectWorkspace(workspace: Workspace, saveToLocalStorage = true) {
    this._workspaceFilter().change(workspace)
    this._clearSelectedUsers()
    this._clearWorkspaceScopedFilters()

    // With "default to all workspaces" on, the selection is not saved so the
    // next load starts on all workspaces again.
    if (saveToLocalStorage && !this._getWorkspaceSelection().allWorkspaces) {
      if (workspace !== Workspace.none && workspace.id) {
        this._lastWorkspaceStorage.save(workspace.id, this._getUserId())
      } else {
        this._lastWorkspaceStorage.save(
          ALL_WORKSPACES_SELECTION,
          this._getUserId(),
        )
      }
      // Lets the next visit tell whether the global workspace changed.
      const globalId = this._getWorkspaceSelection().workspace?.id
      if (globalId) {
        this._storage.setItem(this._globalSnapshotKey, globalId)
      }
    }
  }

  @action.bound
  setSearchText(searchText: string) {
    this._textSearcher.change(searchText)
  }

  @action.bound
  startEditing() {
    this.editing = true
  }

  @action.bound
  stopEditing() {
    this.editing = false
  }
}
